/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Drawer, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { computed } from 'mobx';
import { ICittaStoreLocationsNewData } from './citta-store-locations-new.data';
import { ICittaStoreLocationsNewProps } from './citta-store-locations-new.props.autogenerated';

export interface IStoreLocationsNewState {
    isMobile: boolean;                 // still here if you need it later
    storeCategoryId: string;
    selectedMobileIndex: number | null; // NEW: which store is opened on mobile
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ICittaStoreLocationsNewViewProps extends ICittaStoreLocationsNewProps<ICittaStoreLocationsNewData> {
    isMobileView: boolean;
    CittaStores: IModuleProps;
    StoreTypeHeading: INodeProps;
    StoreHeadingLabel: INodeProps;
    CittaStoreLocations: INodeProps;
    StoreLocationsNode: React.ReactNode; // will hold either desktop or mobile layout
}

/**
 *
 * CittaStoreLocationsNew component
 * @extends {React.PureComponent<ICittaStoreLocationsNewProps<ICittaStoreLocationsNewData>>}
 */
class CittaStoreLocationsNew extends React.PureComponent<
    ICittaStoreLocationsNewProps<ICittaStoreLocationsNewData>,
    IStoreLocationsNewState
> {
    private viewport: GridSize;

    constructor(props: ICittaStoreLocationsNewProps<ICittaStoreLocationsNewData>) {
        super(props);
        this.viewport =
            props.context.request &&
                props.context.request.device &&
                props.context.request.device.Type === 'Mobile'
                ? 'md'
                : 'lg';

        this.state = {
            isMobile: false,
            storeCategoryId: '',
            selectedMobileIndex: null
        };

        this._changeStoreCategoryItem = this._changeStoreCategoryItem.bind(this);
    }

    @computed
    get isMobileView(): boolean {
        return this.viewport === 'md';
    }

    public render(): JSX.Element {
        const isMobileView = this.isMobileView;

        const storeLocationsNode = isMobileView
            ? this._renderStoreLocationsMobile()
            : this._renderStoreLocationsDesktop();

        const viewProps: ICittaStoreLocationsNewViewProps = {
            ...this.props,
            CittaStores: {
                moduleProps: this.props,
                className: 'citta-stores'
            },
            StoreTypeHeading: {
                className: 'store-type-heading'
            },
            StoreHeadingLabel: {
                className: 'store-heading-label',
                tag: 'p'
            },
            CittaStoreLocations: {
                className: 'citta-store-locations',
                role: 'menu'
            },
            isMobileView,
            StoreLocationsNode: storeLocationsNode
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /* ===================== DESKTOP ===================== */

    private _renderStoreLocationsDesktop(): React.ReactNode {
        const { config } = this.props;
        const storeLocationsAddress = config.storeLocationsAddress;
        const isiOS = typeof window !== 'undefined' && /iPad|iPhone/.test(navigator.userAgent);

        if (!storeLocationsAddress || storeLocationsAddress.length === 0) {
            return null;
        }

        return storeLocationsAddress.map((address, index) => {
            const AddressUrl = isiOS
            ? address.googleMapURL
            : address.googleMapURL || '#';
           
            return (
                <div key={index} className="citta-store-1 col-12 col-sm-6 col-md-4 col-lg-4 col-xl-4">
                    <div className="store-locations-hero citta-store-locations-new__desktop-item-image">
                        {this._renderHeaderImage(address.headingImage, true)}
                    </div>

                    <h3 className="store-locations-title">{address.StoreName}</h3>

                    <div className="store-locations-address-main" id={`${address.StoreName}${index}`}>
                        <div>{address.BuildingCompliment == "" ? <br /> : address.BuildingCompliment}</div>
                        <div>{address.StreetNumber}</div>
                        <div>{address.suburb}</div>
                        <div>{address.cityAndpostcode}</div>
                    </div>

                    <Drawer
                        toggleButtonText="Store hours and contact information"
                        openGlyph={'fa fa-chevron-up'}
                        closeGlyph={'fa fa-chevron-down'}
                        glyphPlacement={'end'}
                        collapseProps={{
                            timeout: -1,
                            isOpen:
                                this.state.storeCategoryId ===
                                ((address && address.StoreName) || '') + index ? true : false
                        }}
                        onToggle={() =>
                            (this._changeStoreCategoryItem(
                                ((address && address.StoreName) || '') + index)
                            )
                        }
                    >
                        <>
                            <div
                                className="row citta-store-drawer"
                                style={{
                                    marginLeft: `calc(-${(index % 3)}00% - ${(index % 3)}*15px)`
                                }}
                            >
                                <div className="col-12">
                                    <div className="citta-store-drawer-back">
                                        <a
                                            onClick={() =>
                                                this._changeStoreCategoryItem(
                                                    ((address && address.StoreName) || '') + index
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="citta-store-location-left col-sm-5 col-md-5 col-lg-5 col-xl-5">
                                    <div className="store-locations-name">{address.StoreName}</div>
                                    <br />
                                    {this._renderStoreLocationAddress(address)}
                                    <div className="citta-store-location-end">
                                        <p>Click &amp; collect available during store hours.</p>
                                    </div>
                                </div>
                                <div className="citta-store-location-right col-sm-7 col-md-7 col-lg-7 col-xl-7">
                                    <div className="citta-store-location-right-images row">
                                        <div className="citta-store-location-right-images-1 img-fluid col-sm-6 col-md-6  col-lg-6 col-xl-6">
                                            {this._renderSideImage(address.sideImageLeft, true)}
                                        </div>
                                        <div className="citta-store-location-right-images-1 img-fluid col-sm-6 col-md-6  col-lg-6 col-xl-6">
                                            {this._renderSideImage(address.sideImageRight, true)}
                                        </div>
                                    </div>
                                    <div className="citta-store-map">
                                        <div className="citta-store-location-right-map">
                                            <iframe
                                                src={address.googleMapLink}
                                                width="800"
                                                height="200"
                                                style={{ border: 0 }}
                                            />
                                        </div>
                                        <div>
                                            <a
                                                className="citta-store-map-link"
                                                href={
                                                    AddressUrl
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Get directions &gt;
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="citta-store-drawer-background"
                                style={{
                                    marginLeft: `calc(-${(index % 3)}00% - ${(index % 3)}*15px)`
                                }}
                            />
                        </>
                    </Drawer>
                    <br />
                </div>
            );
        });
    }
    // List + detail controller for mobile
    private _renderStoreLocationsMobile(): React.ReactNode {
        const { config } = this.props;
        const storeLocationsAddress = config.storeLocationsAddress;
        const { selectedMobileIndex } = this.state;

        if (!storeLocationsAddress || storeLocationsAddress.length === 0) {
            return null;
        }

        // If one is selected -> show detail page for that store
        if (
            selectedMobileIndex !== null &&
            storeLocationsAddress[selectedMobileIndex]
        ) {
            const address = storeLocationsAddress[selectedMobileIndex];
            return this._renderStoreLocationsMobileDetail(address);
        }

        // Otherwise show the list of stores
        return (
            <div className="citta-stores-mobile-list">
                {storeLocationsAddress.map((address, index) => (
                    <div key={index} className="citta-store-1 col-12 col-lg-4 col-xl-4">
                        <div className="store-locations-hero">
                            {this._renderHeaderImage(address.headingImage, true)}
                        </div>
                        <div className="store-locations-title">
                            {address.StoreName || ' '}
                        </div>
                        <div className="store-locations-address-main">
                            <div>{address.BuildingCompliment}</div>
                            <div>{address.StreetNumber}</div>
                            <div>{address.suburb}</div>
                            <div>{address.cityAndpostcode}</div>
                        </div>
                        <br />
                        <div className="citta-store-mobile-button">
                            <a
                                type="button"
                                onClick={() => {
                                    this.setState({ selectedMobileIndex: index }, () => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    });
                                }}
                            >
                                MORE INFO &gt;
                            </a>
                        </div>
                        <br />
                    </div>
                ))}
            </div>
        );
    }

    private _renderStoreLocationAddress(address: any): React.ReactNode {
        const weeklyHours = [
            { day: 'Monday', key: 'MondayTime' },
            { day: 'Tuesday', key: 'TuesdayTime' },
            { day: 'Wednesday', key: 'WednesdayTime' },
            { day: 'Thursday', key: 'ThursdayTime' },
            { day: 'Friday', key: 'FridayTime' },
            { day: 'Saturday', key: 'SaturdayTime' },
            { day: 'Sunday', key: 'SundayTime' }
        ];

        return (
            <div className="store-locations-address">
                <div className="citta-store-address">
                    <div>{address.BuildingCompliment}</div>
                    <div>{address.StreetNumber}</div>
                    <div>{address.suburb}</div>
                    <div>{address.cityAndpostcode}</div>
                </div>

                <div className="citta-store-phone">
                    <p className="citta-store-phone-text">phone:</p>
                    <a
                        className="citta-store-phone-number"
                        href={`tel:${address.phone}`}
                    >
                        {address.phone}
                    </a>
                </div>

                <div className="citta-store-email">
                    <p>email:</p>
                    <a
                        className="citta-store-email-link"
                        href={`mailto:${address.emailAddress}`}
                    >
                        {address.emailAddress}
                    </a>
                </div>

                <div className="citta-store-storeHours">
                    <div className="citta-store-storeHours-heading">
                        {this.props.resources.storeStoreHoursHeading}
                    </div>

                    {weeklyHours.map(({ day, key }) => {
                        const time = address[key as keyof typeof address];
                        return (
                            <div key={day}>
                                <div className="citta-store-storeHours-content">
                                    <div className="citta-store-storeHours-content-day">
                                        {day}
                                    </div>
                                    <div className="citta-store-storeHours-content-hours">
                                        {time}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="citta-store-stockistsof">
                    <div className="citta-store-stockistsof-content">
                        <div>
                            {`Our ${address.StoreName} store stocks ${address.stockistOf}.`}
                        </div>
                    </div>
                </div>

                <div className="citta-store-location-notes">
                    {address.locationNotes}
                </div>
            </div>
        );
    }

    private _renderStoreLocationsMobileDetail(address: any): React.ReactNode {
        const isiOS = /iPad|iPhone/.test(navigator.userAgent);
        const AddressUrl = isiOS
            ? address.googleMapURL
            : address.googleMapURL || '#';

        return (
            <div className="citta-stores-mobile">
                <div className="citta-store-location-mobile">
                    <a
                        type="button"
                        className="citta-store-mobile-button"
                        onClick={() => this.setState({ selectedMobileIndex: null })}
                    >
                        &lt; BACK
                    </a>

                    <div className="store-location-mobile-hero">
                        {this._renderHeaderImage(address.headingImage, true)}
                    </div>

                    <div className="citta-store-location-content">
                        <div className="store-location-title">
                            {address.StoreName || ' '}
                        </div>
                        {this._renderStoreLocationAddress(address)}
                        <div className="citta-store-map">
                            <div className="citta-store-location-map">
                                <iframe
                                    src={address.googleMapLink}
                                    width="800"
                                    height="200"
                                    style={{ border: 0 }}
                                />
                            </div>
                            <div>
                                <a
                                    className="citta-store-map-link"
                                    href={
                                        AddressUrl
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Get directions &gt;
                                </a>
                            </div>
                        </div>
                        <div className="citta-store-location-end">
                            Click &amp; collect available during store hours.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ===================== IMAGES ===================== */

    private _renderHeaderImage(
        item?: Msdyn365.IImageData,
        isAriaHiddenAttribute?: boolean
    ): React.ReactNode | null {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=600&h=0&m=6', w: 600, h: 0 },
                sm: { q: 'w=800&h=0&m=6', w: 800, h: 0 },
                md: { q: 'w=1000&h=0&m=6', w: 1000, h: 0 },
                lg: { q: 'w=800&h=0&m=6', w: 800, h: 0 },
                xl: { q: 'w=800&h=0&m=6', w: 800, h: 0 }
            },
            lazyload: true
        };
        const verifiedImageSettings =
            item?.imageSettings?.viewports ? item.imageSettings : defaultImageSettings;

        return (
            <Msdyn365.Image
                {...item}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior="default"
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: item ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
            />
        );
    }

    private _renderSideImage(
        item?: Msdyn365.IImageData,
        isAriaHiddenAttribute?: boolean
    ): React.ReactNode | null {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=600&h=0&m=6', w: 600, h: 0 },
                sm: { q: 'w=600&h=0&m=6', w: 600, h: 0 },
                md: { q: 'w=700&h=0&m=6', w: 700, h: 0 },
                lg: { q: 'w=800&h=0&m=6', w: 800, h: 0 },
                xl: { q: 'w=800&h=0&m=6', w: 800, h: 0 }
            },
            lazyload: true
        };
        const verifiedImageSettings =
            item?.imageSettings?.viewports ? item.imageSettings : defaultImageSettings;

        return (
            <Msdyn365.Image
                {...item}
                requestContext={this.props.context.request}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior="default"
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: item ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
            />
        );
    }

    private _changeStoreCategoryItem(storeCategoryId: string): any {
        if (this.state.storeCategoryId !== storeCategoryId) {
            this.setState({ storeCategoryId });
            setTimeout(() => document.getElementById(storeCategoryId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 30)
        } else {
            this.setState({ storeCategoryId: '' });
            document.getElementById(storeCategoryId);
        }
    }
}

export default CittaStoreLocationsNew;
